import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  Typography,
  withStyles,
  CircularProgress,
} from "@material-ui/core";
import products from "../../service/products";
import DialogError from "../alertas/DialogError";
import { parseDecimal } from "../../libs/format";

const CURRENCIES = ["BRL", "USD", "PYG"];

// Cria/edita um Produto. name é o único obrigatório; demais atributos opcionais
// + um campo de preço por Tabela de Preço do grupo.
// Props: group, priceTables[], product? (edição, com prices[]), close(), save().
class ProductDialog extends React.Component {
  constructor(props) {
    super(props);
    const p = props.product || {};
    // Mapa price_table_id -> preço (string), a partir do product.prices[].
    const priceByTable = {};
    (p.prices || []).forEach((pr) => {
      priceByTable[pr.price_table_id] =
        pr.price == null ? "" : String(pr.price);
    });
    this.state = {
      open: true,
      loading: false,
      name: p.name || "",
      category: p.category || "",
      presentation: p.presentation || "",
      unit_of_measure: p.unit_of_measure || "",
      package_quantity: p.package_quantity == null ? "" : String(p.package_quantity),
      currency: p.currency || "BRL",
      code: p.code || "",
      description: p.description || "",
      active: p.active === undefined ? true : !!p.active,
      priceByTable,
      showError: false,
      error: null,
    };
  }

  handleChange = (name) => (event) => {
    this.setState({ [name]: event.target.value });
  };

  handlePriceChange = (tableId) => (event) => {
    this.setState({
      priceByTable: { ...this.state.priceByTable, [tableId]: event.target.value },
    });
  };

  buildPayload = () => {
    const payload = {
      name: this.state.name,
      category: this.state.category || null,
      presentation: this.state.presentation || null,
      unit_of_measure: this.state.unit_of_measure || null,
      package_quantity: parseDecimal(this.state.package_quantity),
      currency: this.state.currency || null,
      code: this.state.code || null,
      description: this.state.description || null,
      active: this.state.active,
    };
    // Contexto de criação: dentro de um grupo (vincula) ou global (só empresa).
    if (this.props.group) {
      payload.product_group_id = this.props.group.id;
    } else if (this.props.enterpriseId) {
      payload.enterprise_id = this.props.enterpriseId;
    }
    // Só envia os preços preenchidos (upsert por price_table_id).
    const prices = [];
    (this.props.priceTables || []).forEach((table) => {
      const raw = this.state.priceByTable[table.id];
      const value = parseDecimal(raw);
      if (value !== null) {
        prices.push({ price_table_id: table.id, price: value });
      }
    });
    if (prices.length > 0) payload.prices = prices;
    return payload;
  };

  save = async () => {
    if (!this.state.name.trim()) {
      this.setState({
        showError: true,
        error: {
          title: "Nome obrigatório",
          message: "Informe o nome do produto.",
        },
      });
      return;
    }
    this.setState({ loading: true });
    try {
      const payload = this.buildPayload();
      if (this.props.product) {
        await products.updateProduct(this.props.product.id, payload);
      } else {
        await products.createProduct(payload);
      }
      this.props.save();
    } catch (err) {
      this.setState({
        loading: false,
        showError: true,
        error: {
          title: "Erro ao salvar produto",
          message:
            (err && err.data && err.data.message) ||
            "Não foi possível salvar o produto. Tente novamente.",
        },
      });
    }
  };

  render() {
    const { classes, priceTables } = this.props;
    return (
      <Dialog
        open={this.state.open}
        onClose={() => this.props.close()}
        maxWidth="md"
        fullWidth
      >
        <DialogError
          open={this.state.showError}
          onClose={() => this.setState({ showError: false })}
          title={this.state.error ? this.state.error.title : "Erro"}
          message={this.state.error ? this.state.error.message : ""}
          suggestion={"Verifique os campos e tente novamente."}
        />
        <DialogTitle>
          {this.props.product ? "Editar produto" : "Novo produto"}
        </DialogTitle>
        <DialogContent>
          <div className={classes.grid}>
            <TextField
              className={classes.full}
              autoFocus
              label="Nome *"
              value={this.state.name}
              onChange={this.handleChange("name")}
            />
            <TextField
              label="Categoria"
              value={this.state.category}
              onChange={this.handleChange("category")}
            />
            <TextField
              label="Apresentação"
              value={this.state.presentation}
              onChange={this.handleChange("presentation")}
            />
            <TextField
              label="Unidade"
              value={this.state.unit_of_measure}
              onChange={this.handleChange("unit_of_measure")}
            />
            <TextField
              label="Qtd. por embalagem"
              type="number"
              value={this.state.package_quantity}
              onChange={this.handleChange("package_quantity")}
            />
            <FormControl>
              <InputLabel id="currency-label">Moeda</InputLabel>
              <Select
                labelId="currency-label"
                value={this.state.currency}
                onChange={this.handleChange("currency")}
              >
                {CURRENCIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Código (SKU)"
              value={this.state.code}
              onChange={this.handleChange("code")}
            />
            <TextField
              className={classes.full}
              label="Descrição"
              value={this.state.description}
              onChange={this.handleChange("description")}
            />
          </div>

          {priceTables && priceTables.length > 0 && (
            <>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Preços por tabela
              </Typography>
              <div className={classes.grid}>
                {priceTables.map((table) => (
                  <TextField
                    key={table.id}
                    label={table.name}
                    type="number"
                    value={
                      this.state.priceByTable[table.id] == null
                        ? ""
                        : this.state.priceByTable[table.id]
                    }
                    onChange={this.handlePriceChange(table.id)}
                  />
                ))}
              </div>
            </>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={this.state.active}
                onChange={(e) => this.setState({ active: e.target.checked })}
                name="active"
              />
            }
            label="Ativo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.props.close()} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={this.save}
            color="primary"
            disabled={this.state.loading}
          >
            {this.state.loading && (
              <CircularProgress size={20} style={{ marginRight: 8 }} />
            )}
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

const styles = (theme) => ({
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  full: {
    gridColumn: "1 / -1",
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    fontWeight: "bold",
  },
});

export default withStyles(styles)(ProductDialog);
